import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { fmtMoney, rateIncreasePreview, rateIncreaseApply } from "@/lib/api";

export const RateIncrease = ({ token, onApplied }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const openDialog = async () => {
    setResult(null);
    setPreview(null);
    setOpen(true);
    setLoading(true);
    try {
      setPreview(await rateIncreasePreview(token));
    } catch {
      toast.error("Failed to load rate preview");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const res = await rateIncreaseApply(token);
      setResult(res);
      if (res.applied) {
        toast.success(`Rates increased for ${res.updated_count} role(s)`);
        onApplied && onApplied();
      } else if (res.already_applied) {
        toast.info("Rate increase was already applied — no changes made.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to apply rate increase");
    } finally {
      setApplying(false);
    }
  };

  const alreadyApplied = preview?.already_applied || result?.already_applied;

  return (
    <div
      className="rounded-xl border border-white/10 p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      style={{ backgroundColor: "#0F1424" }}
      data-testid="rate-increase-card"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-[#2EE59D]/12 border border-[#2EE59D]/30 p-2.5">
          <TrendingUp size={20} className="text-[#2EE59D]" />
        </div>
        <div>
          <div className="text-white font-semibold">Apply +35% Customer-Facing Rate Increase</div>
          <p className="text-white/45 text-sm mt-0.5 max-w-xl">
            One-time increase to every role&apos;s monthly low/high (×1.35, rounded up to the
            nearest $50). A backup is taken first. Safe to re-run — it never applies twice.
          </p>
        </div>
      </div>
      <Button
        data-testid="rate-increase-open-button"
        onClick={openDialog}
        className="bg-[#2EE59D] hover:bg-[#28cf8c] text-[#062218] font-semibold rounded-lg h-11 shrink-0"
      >
        Review &amp; Apply
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl bg-[#0F1424] border-white/10 text-white max-h-[90vh] overflow-y-auto"
          data-testid="rate-increase-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white text-xl">+35% Role Rate Increase</DialogTitle>
            <DialogDescription className="text-white/45">
              Review old vs. new monthly rates. Experience tiers and in-house comparison
              figures are intentionally unchanged. Nothing is written until you confirm.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-[#2EE59D]" size={26} />
            </div>
          ) : result?.applied ? (
            <div className="space-y-3" data-testid="rate-increase-result">
              <div className="flex items-center gap-2 text-[#2EE59D]">
                <CheckCircle2 size={18} />
                <span className="font-medium">
                  Rates increased for {result.updated_count} role(s).
                  {result.skipped_count ? ` ${result.skipped_count} skipped.` : ""}
                </span>
              </div>
              <p className="text-white/50 text-sm">Backup id: {result.backup_id}</p>
            </div>
          ) : preview ? (
            <div className="space-y-4" data-testid="rate-increase-preview">
              {alreadyApplied && (
                <div className="rounded-lg border border-[#F7C948]/30 bg-[#F7C948]/10 px-4 py-2.5 text-[#F7C948] text-sm">
                  This increase has already been applied. Re-running will make no changes.
                </div>
              )}
              <div className="flex gap-3 text-sm">
                <Badge className="border bg-[#2EE59D]/10 text-[#2EE59D] border-[#2EE59D]/25">
                  {preview.eligible_count} role(s) eligible
                </Badge>
                {preview.skipped_count > 0 && (
                  <Badge className="border bg-[#FF5C7A]/10 text-[#FF5C7A] border-[#FF5C7A]/25">
                    {preview.skipped_count} skipped (invalid rates)
                  </Badge>
                )}
              </div>

              <div className="rounded-lg border border-white/10 overflow-hidden max-h-[46vh] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/60">Role</TableHead>
                      <TableHead className="text-white/60">Current</TableHead>
                      <TableHead className="text-white/60">New</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map((r) => (
                      <TableRow key={r.key} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white">{r.name}</TableCell>
                        <TableCell className="text-white/60 tabular-nums">
                          {fmtMoney(r.old_low)} – {fmtMoney(r.old_high)}
                        </TableCell>
                        <TableCell className="text-[#2EE59D] tabular-nums font-medium">
                          <span className="inline-flex items-center gap-1">
                            <ArrowRight size={12} className="text-white/30" />
                            {fmtMoney(r.new_low)} – {fmtMoney(r.new_high)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-white/40 text-xs">{preview.tiers_note}</p>
            </div>
          ) : null}

          {!result && !loading && (
            <DialogFooter className="mt-2">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                className="text-white/70 hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                data-testid="rate-increase-confirm-button"
                onClick={apply}
                disabled={applying || alreadyApplied}
                className="bg-[#2EE59D] hover:bg-[#28cf8c] text-[#062218] font-semibold rounded-lg disabled:opacity-50"
              >
                {applying ? <Loader2 className="animate-spin mr-2" size={15} /> : null}
                {alreadyApplied ? "Already Applied" : "Confirm & Apply +35%"}
              </Button>
            </DialogFooter>
          )}
          {result && (
            <DialogFooter className="mt-2">
              <Button
                onClick={() => setOpen(false)}
                className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg"
              >
                Done
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
